// usePagedQuery.js — shared hook for SERVER-SIDE paginated tables.
//
//   const t = usePagedQuery(`${API}/api/something`, { search, status, ...dateRangeParams(f, 'created') });
//   t.rows / t.total / t.page / t.totalPages / t.loading / t.setPage / t.refetch
//
// - Filters live in `params`; whenever they change the table goes back to page 1 and
//   refetches (pagination and filters always agree).
// - Out-of-date responses are discarded (fast typing / quick page clicks can't overwrite
//   newer results) and in-flight requests are aborted.
// - The server clamps a page that is past the end; the hook follows it.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveDateRange } from './DateFilter';

export const DEFAULT_PAGE_SIZE = 25;

// DateFilter state -> { <name>_from, <name>_to, <name>_blank } query params.
export function dateRangeParams(state, name, { includeBlank = false } = {}) {
    const range = resolveDateRange(state);
    if (!range) return {};
    const out = {};
    if (range[0]) out[`${name}_from`] = range[0];
    if (range[1]) out[`${name}_to`] = range[1];
    if (includeBlank || (state && state.includeBlank)) out[`${name}_blank`] = '1';
    return out;
}

export function buildQuery(params) {
    const sp = new URLSearchParams();
    Object.keys(params || {}).sort().forEach((k) => {
        const v = params[k];
        if (v === undefined || v === null || v === '' || v === false) return;
        sp.append(k, Array.isArray(v) ? v.join(',') : String(v));
    });
    return sp.toString();
}

export function usePagedQuery(url, params = {}, { pageSize: initialPageSize = DEFAULT_PAGE_SIZE, enabled = true } = {}) {
    const [pageSize, setPageSizeState] = useState(initialPageSize);
    const filterKey = useMemo(() => buildQuery(params), [params]);   // eslint-disable-line react-hooks/exhaustive-deps
    // The page belongs to the filter set (and page size) it was chosen under. Whenever that key changes the
    // stored page is reset to 1 right away, so coming back to an earlier filter set (e.g. "Clear") never
    // resurrects a stale page number.
    const stateKey = filterKey + '|' + pageSize;
    const [pageState, setPageState] = useState({ key: stateKey, page: 1 });
    if (pageState.key !== stateKey) setPageState({ key: stateKey, page: 1 });
    const page = pageState.key === stateKey ? pageState.page : 1;

    const [data, setData] = useState({ rows: [], total: 0, totalPages: 1, extras: {} });
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState(null);
    const reqId = useRef(0);
    const [tick, setTick] = useState(0);

    const setPage = useCallback((p) => setPageState({ key: stateKey, page: Math.max(1, Number(p) || 1) }), [stateKey]);
    const setPageSize = useCallback((n) => setPageSizeState(Math.max(1, Number(n) || DEFAULT_PAGE_SIZE)), []);
    const refetch = useCallback(() => setTick((t) => t + 1), []);

    useEffect(() => {
        if (!enabled) { setLoading(false); return undefined; }
        const id = ++reqId.current;
        const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        setLoading(true);
        const qs = buildQuery({ ...params, page, page_size: pageSize });
        fetch(`${url}?${qs}`, ctrl ? { signal: ctrl.signal } : undefined)
            .then(async (res) => {
                const body = await res.json().catch(() => null);
                if (!res.ok) alert((body && body.error) || `Request failed (${res.status})`);
                return body;
            })
            .then((body) => {
                if (id !== reqId.current) return;           // stale
                const { rows, total, page: servedPage, pageSize: servedSize, totalPages, ...extras } = body || {};
                void servedSize;
                setData({ rows: Array.isArray(rows) ? rows : [], total: Number(total) || 0, totalPages: Number(totalPages) || 1, extras });
                setError(null);
                if (servedPage && servedPage !== page) setPageState({ key: stateKey, page: servedPage });
            })
            .catch((err) => {
                if (err && err.name === 'AbortError') return;
                if (id !== reqId.current) return;
                console.error('Failed to load table data:', err);
                setError(err.message || 'Failed to load data');
            })
            .finally(() => { if (id === reqId.current) setLoading(false); });
        return () => { if (ctrl) ctrl.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, filterKey, page, pageSize, enabled, tick]);

    // Replace the visible rows (used by pages whose handlers update rows optimistically with a setState-style updater).
    const setRows = useCallback((updater) => {
        setData((d) => ({ ...d, rows: typeof updater === 'function' ? updater(d.rows) : updater }));
    }, []);

    // Every row matching the current filters (all=1, capped server-side) — for CSV / PDF export.
    const paramsRef = useRef(params);
    paramsRef.current = params;
    const fetchAll = useCallback(async () => {
        const res = await fetch(`${url}?${buildQuery({ ...paramsRef.current, all: 1 })}`);
        const body = await res.json().catch(() => null);
        if (!res.ok) alert((body && body.error) || `Request failed (${res.status})`);
        return Array.isArray(body && body.rows) ? body.rows : [];
    }, [url]);

    // Optimistic in-place edits (e.g. a cell saved inline) without refetching the page.
    const patchRow = useCallback((predicate, patch) => {
        setData((d) => ({ ...d, rows: d.rows.map((r) => (predicate(r) ? { ...r, ...(typeof patch === 'function' ? patch(r) : patch) } : r)) }));
    }, []);

    return {
        rows: data.rows, total: data.total, totalPages: data.totalPages, extras: data.extras,
        page, pageSize, loading, error,
        setPage, setPageSize, refetch, patchRow, setRows, fetchAll
    };
}

export default usePagedQuery;